/**
 * SignatureBuilder — fill a few fields, pick a template, click Use.
 *
 * Why it exists: pasting Gmail HTML requires DevTools know-how. Most builders
 * won't do that. This component lets them input structured data and produces
 * professional, email-safe HTML (inline styles, table-based layout — so Gmail
 * and Outlook render it cleanly).
 *
 * Also includes a "Send test to yourself" action so users can verify the
 * rendering end-to-end before committing.
 */
import React, { useMemo, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Wand2, Send, Check } from 'lucide-react';
import { toast } from 'sonner';
import { emailAPI } from '../services/api';

// Simple email-safe HTML signature templates. Inline styles only (Gmail strips
// <style> blocks). Table-based layout for consistency across clients.
const escape = (v) => String(v || '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
const linkOrText = (val) => {
  if (!val) return '';
  const v = escape(val);
  if (val.startsWith('http')) return `<a href="${v}" style="color:#1E3A5F;text-decoration:none;">${v}</a>`;
  if (val.includes('@')) return `<a href="mailto:${v}" style="color:#1E3A5F;text-decoration:none;">${v}</a>`;
  return v;
};

const TEMPLATES = {
  classic: {
    label: 'Classic',
    render: (d) => `
<table cellpadding="0" cellspacing="0" border="0" style="font-family:Arial,sans-serif;font-size:13px;color:#111;line-height:1.45;">
  <tr>
    ${d.logo_url ? `<td style="padding-right:16px;vertical-align:top;"><img src="${escape(d.logo_url)}" alt="${escape(d.company_name)}" width="80" style="display:block;border:0;"/></td>` : ''}
    <td style="vertical-align:top;border-left:${d.logo_url ? '3px solid #F97316' : 'none'};padding-left:${d.logo_url ? '16px' : '0'};">
      <div style="font-size:15px;font-weight:bold;color:#1E3A5F;">${escape(d.name)}</div>
      ${d.title ? `<div style="color:#555;">${escape(d.title)}</div>` : ''}
      ${d.company_name ? `<div style="font-weight:bold;margin-top:2px;">${escape(d.company_name)}</div>` : ''}
      <div style="margin-top:6px;">
        ${d.phone ? `<div>P: ${linkOrText('tel:' + d.phone)} <span style="color:#888;">${escape(d.phone)}</span></div>` : ''}
        ${d.email ? `<div>E: ${linkOrText(d.email)}</div>` : ''}
        ${d.website ? `<div>W: ${linkOrText(d.website)}</div>` : ''}
      </div>
      ${d.tagline ? `<div style="margin-top:6px;color:#888;font-style:italic;">${escape(d.tagline)}</div>` : ''}
    </td>
  </tr>
</table>`.trim(),
  },
  modern: {
    label: 'Modern',
    render: (d) => `
<table cellpadding="0" cellspacing="0" border="0" style="font-family:Helvetica,Arial,sans-serif;font-size:13px;color:#222;">
  <tr>
    <td style="padding:12px 0;border-top:2px solid #1E3A5F;border-bottom:2px solid #1E3A5F;">
      <table cellpadding="0" cellspacing="0" border="0">
        <tr>
          ${d.logo_url ? `<td style="padding-right:18px;vertical-align:middle;"><img src="${escape(d.logo_url)}" alt="${escape(d.company_name)}" width="70" style="display:block;border:0;"/></td>` : ''}
          <td style="vertical-align:middle;">
            <div style="font-size:16px;font-weight:600;color:#1E3A5F;letter-spacing:0.3px;">${escape(d.name)}</div>
            ${d.title ? `<div style="color:#F97316;font-weight:600;text-transform:uppercase;font-size:11px;letter-spacing:1.2px;margin-top:2px;">${escape(d.title)}${d.company_name ? ' · ' + escape(d.company_name) : ''}</div>` : (d.company_name ? `<div style="color:#F97316;font-weight:600;text-transform:uppercase;font-size:11px;letter-spacing:1.2px;margin-top:2px;">${escape(d.company_name)}</div>` : '')}
            <div style="margin-top:8px;color:#333;">
              ${[
                d.phone ? `<span>${escape(d.phone)}</span>` : '',
                d.email ? linkOrText(d.email) : '',
                d.website ? linkOrText(d.website) : '',
              ].filter(Boolean).join(' &nbsp;·&nbsp; ')}
            </div>
          </td>
        </tr>
      </table>
    </td>
  </tr>
</table>`.trim(),
  },
  minimal: {
    label: 'Minimal',
    render: (d) => `
<div style="font-family:Arial,sans-serif;font-size:13px;color:#333;line-height:1.5;">
  <div style="font-weight:bold;color:#111;">${escape(d.name)}${d.title ? `<span style="color:#888;font-weight:normal;"> &nbsp;|&nbsp; ${escape(d.title)}</span>` : ''}</div>
  ${d.company_name ? `<div style="color:#1E3A5F;font-weight:bold;">${escape(d.company_name)}</div>` : ''}
  <div style="color:#555;margin-top:4px;">
    ${[d.phone, d.email ? linkOrText(d.email) : '', d.website ? linkOrText(d.website) : ''].filter(Boolean).join(' &nbsp;·&nbsp; ')}
  </div>
</div>`.trim(),
  },
};

export const SignatureBuilder = ({ open, onClose, branding, currentLogo, onSave }) => {
  const [fields, setFields] = useState({
    name: '',
    title: '',
    company_name: branding?.company_name || '',
    phone: branding?.phone || '',
    email: branding?.email || '',
    website: branding?.website || '',
    tagline: '',
    logo_url: currentLogo || '',
  });
  const [template, setTemplate] = useState('classic');

  const generated = useMemo(() => TEMPLATES[template].render(fields), [fields, template]);

  const update = (k, v) => setFields((prev) => ({ ...prev, [k]: v }));

  const handleUse = () => {
    onSave(generated);
    toast.success('Signature applied — remember to click Save Settings.');
    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-5xl" data-testid="signature-builder-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Wand2 className="h-5 w-5" /> Build Your Email Signature
          </DialogTitle>
        </DialogHeader>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Form */}
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Your Name *</Label><Input value={fields.name} onChange={(e) => update('name', e.target.value)} placeholder="Nick Smith" data-testid="sig-name" /></div>
              <div><Label>Job Title</Label><Input value={fields.title} onChange={(e) => update('title', e.target.value)} placeholder="Director" data-testid="sig-title" /></div>
              <div className="col-span-2"><Label>Company</Label><Input value={fields.company_name} onChange={(e) => update('company_name', e.target.value)} placeholder="Formiq Building" data-testid="sig-company" /></div>
              <div><Label>Phone</Label><Input value={fields.phone} onChange={(e) => update('phone', e.target.value)} placeholder="0433 288 566" data-testid="sig-phone" /></div>
              <div><Label>Email</Label><Input type="email" value={fields.email} onChange={(e) => update('email', e.target.value)} placeholder="you@company.com" data-testid="sig-email" /></div>
              <div className="col-span-2"><Label>Website</Label><Input value={fields.website} onChange={(e) => update('website', e.target.value)} placeholder="https://yourcompany.com" data-testid="sig-website" /></div>
              <div className="col-span-2"><Label>Tagline (optional)</Label><Input value={fields.tagline} onChange={(e) => update('tagline', e.target.value)} placeholder="QBCC 15307919  ·  ABN 34 559 921 291" data-testid="sig-tagline" /></div>
              <div className="col-span-2"><Label>Logo URL (optional)</Label><Input value={fields.logo_url} onChange={(e) => update('logo_url', e.target.value)} placeholder="Uses your company logo by default" data-testid="sig-logo" /></div>
            </div>

            <div className="pt-2">
              <Label className="mb-2 block">Template</Label>
              <div className="flex gap-2 flex-wrap" data-testid="sig-template-picker">
                {Object.entries(TEMPLATES).map(([key, t]) => (
                  <button
                    key={key}
                    type="button"
                    onClick={() => setTemplate(key)}
                    className={`px-4 py-2 rounded-sm text-sm border transition-colors ${
                      template === key
                        ? 'bg-primary text-primary-foreground border-primary'
                        : 'bg-input border-border hover:border-primary/60'
                    }`}
                    data-testid={`sig-template-${key}`}
                  >
                    {template === key && <Check className="inline h-3 w-3 mr-1" />}
                    {t.label}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Preview */}
          <div className="space-y-2">
            <Label>Live Preview</Label>
            <div className="border border-border rounded-sm bg-white p-6 min-h-[240px]" data-testid="sig-preview">
              <div dangerouslySetInnerHTML={{ __html: generated }} />
            </div>
            <p className="text-xs text-muted-foreground">
              This is email-safe HTML (inline styles, table layout). Renders consistently across Gmail, Outlook, Apple Mail.
            </p>
          </div>
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={onClose} data-testid="sig-cancel-btn">Cancel</Button>
          <Button onClick={handleUse} disabled={!fields.name.trim()} data-testid="sig-use-btn">
            <Check className="h-4 w-4 mr-2" /> Use This Signature
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

/**
 * TestEmailButton — sends a sample email to a user-provided address using the
 * CURRENTLY-SAVED branding + signature, so builders can verify rendering.
 */
export const TestEmailButton = ({ defaultEmail, companyName }) => {
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState(defaultEmail || '');
  const [sending, setSending] = useState(false);

  const handleSend = async () => {
    if (!email) {
      toast.error('Please enter a recipient email');
      return;
    }
    setSending(true);
    try {
      await emailAPI.send({
        recipient_email: email,
        subject: `Test email from ${companyName || 'your business'}`,
        html_content: `
          <p>Hi there,</p>
          <p>This is a test email from <strong>${companyName || 'your business'}</strong>.</p>
          <p>If everything looks right — sender name, signature, formatting — you're all set to start sending quotes, POs and invoices from ConstructOne.</p>
          <p>Cheers,</p>
        `,
        related_type: 'branding_test',
      });
      toast.success(`Test email sent to ${email} — check your inbox (and spam folder).`);
      setOpen(false);
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Failed to send — is your Gmail app password configured in backend/.env?');
    } finally {
      setSending(false);
    }
  };

  return (
    <>
      <Button variant="outline" size="sm" onClick={() => setOpen(true)} data-testid="test-email-open-btn">
        <Send className="h-4 w-4 mr-2" /> Send Test Email
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md" data-testid="test-email-dialog">
          <DialogHeader>
            <DialogTitle>Send a test email</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <p className="text-sm text-muted-foreground">
              We'll send a short sample email with your current branding and signature so you can confirm how it looks in the wild.
            </p>
            <div>
              <Label>Recipient email</Label>
              <Input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@company.com"
                data-testid="test-email-recipient-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={handleSend} disabled={sending} data-testid="test-email-send-btn">
              {sending ? 'Sending…' : <><Send className="h-4 w-4 mr-2" /> Send Test</>}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};
